/**
 * EL CÓDIGO DE BARRAS DEL DNI ARGENTINO, interpretado.
 *
 * Responsabilidad única: dado el texto que devolvió un decodificador de PDF417,
 * decir qué campos trae. No decodifica nada —eso es un adaptador, como el OCR—
 * y por eso se puede probar sin wasm y sin navegador.
 *
 * VERIFICADO sobre un DNI real (familia 2012), no supuesto: el payload son
 * campos separados por "@". Un ejemplo con datos inventados:
 *
 *   00123456789@PEREZ@JUAN CARLOS@M@12345678@A@19/04/1987@15/03/2015@205
 *
 * Por qué esta fuente vale más que el OCR: el PDF417 lleva corrección de errores
 * Reed-Solomon. O decodifica bien, o no decodifica. No devuelve un "8" cuando
 * dice "B", que es el modo en que nos falló el OCR del MRZ.
 *
 * Y por qué se parsea POR FORMA y no por posición: los modelos cambian el orden
 * y la cantidad de campos. Reconocer cada campo por cómo se ve sobrevive a eso;
 * contar posiciones, no.
 */
import { Hallazgo } from "./hallazgos";

const FECHA = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function aIso(dmy: string): string | null {
  const m = dmy.match(FECHA);
  if (!m) return null;
  const [, d, mes, a] = m;
  const anio = parseInt(a, 10);
  const mm = parseInt(mes, 10);
  const dd = parseInt(d, 10);
  if (mm < 1 || mm > 12 || dd < 1) return null;
  if (dd > new Date(Date.UTC(anio, mm, 0)).getUTCDate()) return null;
  return `${a}-${mes}-${d}`;
}

export interface CamposDelCodigo {
  numeroTramite: string | null;
  apellido: string | null;
  nombres: string | null;
  sexo: "M" | "F" | "X" | null;
  numeroDocumento: string | null;
  ejemplar: string | null;
  fechaNacimiento: string | null;
  fechaEmision: string | null;
}

/**
 * Interpreta el payload.
 *
 * Cada campo se reconoce por su forma:
 *   - once dígitos           → número de trámite
 *   - siete u ocho dígitos   → número de documento
 *   - una sola letra M/F/X   → sexo
 *   - dd/mm/aaaa             → fecha; la más vieja es el nacimiento
 *   - una sola letra (otra)  → ejemplar
 *   - texto alfabético       → apellido el primero, nombres el segundo
 */
export function interpretarCodigoDni(payload: string): CamposDelCodigo | null {
  const partes = String(payload ?? "").split("@").map((p) => p.trim());
  if (partes.length < 4) return null;

  const salida: CamposDelCodigo = {
    numeroTramite: null, apellido: null, nombres: null, sexo: null,
    numeroDocumento: null, ejemplar: null, fechaNacimiento: null, fechaEmision: null,
  };
  const fechas: string[] = [];
  const textos: string[] = [];

  for (const p of partes) {
    if (/^\d{11}$/.test(p)) { salida.numeroTramite ??= p; continue; }
    if (/^\d{7,8}$/.test(p)) { const sinCeros = p.replace(/^0+/, ""); if (sinCeros) salida.numeroDocumento ??= sinCeros; continue; }
    if (/^[MFX]$/.test(p)) { salida.sexo ??= p as "M" | "F" | "X"; continue; }
    const iso = aIso(p);
    if (iso) { fechas.push(iso); continue; }
    if (/^[A-Z]$/.test(p)) { salida.ejemplar ??= p; continue; }
    if (/[A-ZÁÉÍÓÚÑ]{2,}/i.test(p)) { textos.push(p.toUpperCase()); continue; }
  }

  fechas.sort();
  salida.fechaNacimiento = fechas[0] ?? null;
  salida.fechaEmision = fechas.length > 1 ? fechas[fechas.length - 1] : null;
  salida.apellido = textos[0] ?? null;
  salida.nombres = textos[1] ?? null;

  // Sin un identificador numérico esto no es el código de un documento: puede
  // ser cualquier texto con arrobas. Pedir solo "algún campo" aceptaba basura.
  const esUnDocumento = salida.numeroDocumento !== null || salida.numeroTramite !== null;
  return esUnDocumento ? salida : null;
}

/**
 * Los campos del código como hallazgos.
 *
 * Van marcados `probado`: la corrección de errores del PDF417 cumple el mismo
 * papel que un dígito de control. Si decodificó, dice lo que el documento tiene
 * grabado.
 *
 * El número de trámite se devuelve como cualquier otro campo, y quien recibe
 * esto decide qué hacer con él. Tiene una consecuencia que conviene no
 * perder de vista: si está en el código, tapar el número impreso NO lo protege.
 */
export function hallazgosDelCodigo(payload: string): Hallazgo[] {
  const c = interpretarCodigoDni(payload);
  if (!c) return [];
  const salida: Hallazgo[] = [];
  const poner = (campo: string, valor: string | null) => {
    if (!valor) return;
    salida.push({ campo, valor, crudo: valor, origen: "pdf417", probado: true });
  };
  poner("numeroTramite", c.numeroTramite);
  poner("apellido", c.apellido);
  poner("nombres", c.nombres);
  poner("sexo", c.sexo);
  poner("numeroDocumento", c.numeroDocumento);
  poner("ejemplar", c.ejemplar);
  poner("fechaNacimiento", c.fechaNacimiento);
  poner("fechaEmision", c.fechaEmision);
  return salida;
}

/**
 * Cómo buscar el código en una foto, en orden de lo que funcionó.
 *
 * MEDIDO sobre un DNI real de 1280x720: la imagen entera NO decodifica; la mitad
 * derecha sin preproceso, ampliada al doble, SÍ. Y un PDF417 sintético del mismo
 * contenido se lee hasta 320 px de ancho, así que la resolución importa mucho
 * menos de lo que se suele decir: lo que estorba es el resto de la foto.
 *
 * Las zonas van en fracciones de la IMAGEN, no de la tarjeta, porque esto corre
 * antes de saber si hay tarjeta y dónde. Cuando la tarjeta se ubique, el
 * catálogo dará la zona exacta y esta escalera sobra.
 */
export interface IntentoDeDecodificacion {
  nombre: string;
  zona: { x: number; y: number; w: number; h: number };
  escala: number;
  gris?: boolean;
  contraste?: boolean;
}

export const INTENTOS_DE_DECODIFICACION: IntentoDeDecodificacion[] = [
  { nombre: "mitad derecha x2", zona: { x: 0.5, y: 0.55, w: 0.5, h: 0.45 }, escala: 2 },
  // El QR del modelo 2026 vive arriba a la derecha del DORSO, no abajo como el
  // PDF417 del 2012. Sin esta zona ninguna de las otras lo cubría.
  { nombre: "QR del dorso 2026", zona: { x: 0.62, y: 0.15, w: 0.38, h: 0.55 }, escala: 2 },
  { nombre: "imagen entera", zona: { x: 0, y: 0, w: 1, h: 1 }, escala: 1 },
  { nombre: "imagen entera x2", zona: { x: 0, y: 0, w: 1, h: 1 }, escala: 2 },
  { nombre: "franja inferior x2", zona: { x: 0, y: 0.6, w: 1, h: 0.4 }, escala: 2 },
  { nombre: "mitad derecha gris x3", zona: { x: 0.5, y: 0.55, w: 0.5, h: 0.45 }, escala: 3, gris: true },
  { nombre: "cuadrante inf. der. x3", zona: { x: 0.55, y: 0.66, w: 0.45, h: 0.32 }, escala: 3 },
  { nombre: "mitad superior x2", zona: { x: 0, y: 0, w: 1, h: 0.5 }, escala: 2 },
];
